import { useEffect } from 'react';
import { Link, useMatch } from 'react-router';
import { useBannerStore, type CommunityBanner } from '@/state/bannerStore';
import { selectIsOffline, useConnectionStore } from '@/state/connectionStore';
import { Icon } from '@/ui/kit/Icon';
import styles from './Overlays.module.css';

import { useT } from '@/i18n/languageStore';

function communityTarget(banner: CommunityBanner): string | null {
  switch (banner.targetType) {
    case 'post':
      return banner.targetId ? `/posts/${banner.targetId}` : null;
    case 'event':
      return '/events';
    case 'promotion':
      return '/promotions';
    case 'announcement':
      return '/announcements';
    default:
      return null;
  }
}

/** FR-029: a new message elsewhere in the app. Hidden when that conversation is already open. */
function IncomingMessageBanner() {
  const t = useT();
  const banner = useBannerStore((s) => s.message);
  const dismiss = useBannerStore((s) => s.dismissMessage);
  const openChat = useMatch('/messages/:conversationId');
  if (!banner || openChat?.params.conversationId === banner.conversationId) {
    return null;
  }
  return (
    <div className={styles.banner}>
      <Link
        to={`/messages/${banner.conversationId}`}
        className={styles.body}
        aria-label={t('New message from {senderName}: {preview}', {
          senderName: banner.senderName,
          preview: banner.preview,
        })}
        onClick={dismiss}
      >
        <span className={styles.title}>
          {t('Kindra · {senderName}', { senderName: banner.senderName })}
        </span>
        <span className={styles.preview}>{banner.preview}</span>
      </Link>
      <button type="button" className={styles.dismiss} aria-label={t('Dismiss')} onClick={dismiss}>
        <Icon name="close" size={18} />
      </button>
    </div>
  );
}

/** FR-030: community updates and admin announcements; opens the related page if there is one. */
function CommunityUpdateBanner() {
  const t = useT();
  const banner = useBannerStore((s) => s.community);
  const dismiss = useBannerStore((s) => s.dismissCommunity);
  if (!banner) {
    return null;
  }
  const target = communityTarget(banner);
  const label = t('Community update: {text}', { text: banner.text });
  const content = (
    <>
      <span className={styles.title}>
        {t('Kindra ·')} {banner.title ?? t('Community update')}
      </span>
      <span className={styles.preview}>{t(banner.text)}</span>
    </>
  );
  return (
    <div className={styles.banner}>
      {target ? (
        <Link to={target} className={styles.body} aria-label={t(label)} onClick={dismiss}>
          {content}
        </Link>
      ) : (
        <button type="button" className={styles.body} aria-label={t(label)} onClick={dismiss}>
          {content}
        </button>
      )}
      <button type="button" className={styles.dismiss} aria-label={t('Dismiss')} onClick={dismiss}>
        <Icon name="close" size={18} />
      </button>
    </div>
  );
}

const OFFER_BANNER_MS = 6000;

/** New offers on the member's own posts. Hidden while they are looking at that post; takes itself down. */
function NewOffersBanner() {
  const t = useT();
  const banner = useBannerStore((s) => s.offers);
  const dismiss = useBannerStore((s) => s.dismissOffers);
  const viewing = useMatch('/posts/:postId');
  useEffect(() => {
    if (!banner) return;
    const timer = setTimeout(dismiss, OFFER_BANNER_MS);
    return () => clearTimeout(timer);
  }, [banner, dismiss]);
  if (!banner || (banner.postId && viewing?.params.postId === banner.postId)) {
    return null;
  }
  const text = t('{v} on your posts', {
    v: banner.count === 1 ? t('1 new offer') : t('{count} new offers', { count: banner.count }),
  });
  return (
    <div className={styles.banner}>
      <Link
        to={banner.postId ? `/posts/${banner.postId}` : '/profile/posts'}
        className={styles.body}
        aria-label={t(text)}
        onClick={dismiss}
      >
        <span className={styles.title}>
          {t('Kindra ·')} {banner.count === 1 ? t('New offer') : t('New offers')}
        </span>
        <span className={styles.preview}>{t(text)}</span>
      </Link>
      <button type="button" className={styles.dismiss} aria-label={t('Dismiss')} onClick={dismiss}>
        <Icon name="close" size={18} />
      </button>
    </div>
  );
}

/** WEB-010: live updates dropped; drafts are kept and missed messages arrive on reconnect. */
function OfflineNotice() {
  const t = useT();
  const offline = useConnectionStore(selectIsOffline);
  return (
    <div role="status" aria-label={t('Connection')} className={styles.offlineRegion}>
      {offline ? (
        <p className={styles.offline}>
          {t('You’re offline. Messages will arrive when the connection is back.')}
        </p>
      ) : null}
    </div>
  );
}

/** Floats above every page of the signed-in app. The regions stay mounted so screen readers hear new content. */
export function Overlays() {
  const t = useT();
  return (
    <>
      <div
        role="status"
        aria-label={t('Notifications')}
        aria-live="polite"
        className={styles.region}
      >
        <IncomingMessageBanner />
        <CommunityUpdateBanner />
        <NewOffersBanner />
      </div>
      <OfflineNotice />
    </>
  );
}
